import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const NaNoWriMoCalculator = () => {
  const [writingData, setWritingData] = useState({
    currentWordCount: '',
    daysWritten: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWritingData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateProgress = () => {
    const { currentWordCount, daysWritten } = writingData;

    const currentWordCountNum = parseInt(currentWordCount, 10) || 0;
    const daysWrittenNum = parseInt(daysWritten, 10) || 0;

    if (currentWordCountNum === 0 || daysWrittenNum === 0) {
      setError('Please enter valid word count and days written');
      return;
    }

    const wordsPerDay = currentWordCountNum / daysWrittenNum;
    const daysRemaining = 30 - daysWrittenNum;
    const wordsNeededPerDay = (50000 - currentWordCountNum) / daysRemaining;

    const newResults = {
      wordsPerDay,
      daysRemaining,
      wordsNeededPerDay
    };

    setResults(newResults);

    setChartData({
      labels: ['Words Written', 'Words Remaining'],
      datasets: [
        {
          label: 'Word Count Breakdown',
          data: [currentWordCountNum, 50000 - currentWordCountNum],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(255, 99, 132, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 99, 132, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const exampleData = {
    currentWordCount: 10000,
    daysWritten: 10,
    wordsPerDay: 1000,
    daysRemaining: 20,
    wordsNeededPerDay: 2000
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>NaNoWriMo Calculator | Track Your Writing Progress</title>
        <meta name="description" content="Track your NaNoWriMo progress with our calculator. Calculate daily word count requirements and visualize your progress." />
        <meta name="keywords" content="NaNoWriMo calculator, writing progress, daily word count, writing tracker" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">✍️</div>
            <h1 className="text-3xl font-bold text-gray-800">NaNoWriMo Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The NaNoWriMo Calculator helps you track your progress during National Novel Writing Month. Enter your current word count and the number of days you've written to see how you're doing.</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Word Count:</label>
                <input
                  type="number"
                  id="currentWordCount"
                  name="currentWordCount"
                  value={writingData.currentWordCount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your current word count"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Days Written:</label>
                <input
                  type="number"
                  id="daysWritten"
                  name="daysWritten"
                  value={writingData.daysWritten}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter the number of days you've written"
                />
              </div>
            </div>

            <button
              onClick={calculateProgress}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Progress
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Writing Progress Analysis</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Current Progress</h3>
                    <p className="text-gray-600">Current Word Count: {results.currentWordCount}</p>
                    <p className="text-gray-600">Days Written: {results.daysWritten}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Daily Word Count</h3>
                    <p className="text-gray-600">Words Per Day: {results.wordsPerDay.toFixed(2)}</p>
                    <p className="text-gray-600">Days Remaining: {results.daysRemaining}</p>
                    <p className="text-gray-600">Words Needed Per Day: {results.wordsNeededPerDay.toFixed(2)}</p>
                  </div>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Word Count'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Categories'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Word Count Breakdown',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 mb-6">
                Here's an example calculation using sample data:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4">Sample Writing Data:</h4>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Current Word Count: {exampleData.currentWordCount}</li>
                  <li>Days Written: {exampleData.daysWritten}</li>
                </ul>
                
                <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
                <ol className="list-decimal pl-8 space-y-4">
                  <li>
                    <p>Words Per Day Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Words Per Day = Current Word Count / Days Written = {exampleData.currentWordCount} / {exampleData.daysWritten} = {exampleData.wordsPerDay}</li>
                    </ul>
                  </li>
                  <li>
                    <p>Days Remaining Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Days Remaining = Total Days - Days Written = 30 - {exampleData.daysWritten} = {exampleData.daysRemaining}</li>
                    </ul>
                  </li>
                  <li>
                    <p>Words Needed Per Day Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Words Needed Per Day = (Total Word Count - Current Word Count) / Days Remaining = (50000 - {exampleData.currentWordCount}) / {exampleData.daysRemaining} = {exampleData.wordsNeededPerDay}</li>
                    </ul>
                  </li>
                </ol>
                
                <p className="mt-6 font-semibold">Interpretation:</p>
                <p>In this example, you have written {exampleData.currentWordCount} words in {exampleData.daysWritten} days. To reach the 50,000-word goal in the remaining {exampleData.daysRemaining} days, you need to write approximately {exampleData.wordsNeededPerDay} words per day.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding NaNoWriMo <span className="text-blue-600">📊</span></h2>
            <div className="text-gray-600 mb-8">
              <p>NaNoWriMo, or National Novel Writing Month, is a challenge to write a 50,000-word novel in the month of November. This calculator helps you track your progress by:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Calculating your average words per day</li>
                <li>Showing how many days you have left</li>
                <li>Estimating the daily word count needed to reach 50,000 words</li>
              </ul>
              <p className="italic mt-2">Note: Consistency is key! Try to write every day to stay on track.</p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use NaNoWriMo Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the NaNoWriMo Calculator effectively:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Your Data</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter your current word count</li>
                  <li>Enter the number of days you've written</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Calculate Progress</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate Progress" to generate the results</li>
                  <li>View your average words per day, days remaining, and words needed per day</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Interpret Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Analyze your progress and adjust your writing schedule accordingly</li>
                  <li>Use the chart to visualize your word count breakdown</li>
                </ul>
              </div>

              <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Stay consistent and try to write every day</li>
                  <li>Adjust your daily word count goal if you fall behind or get ahead</li>
                  <li>Use this tool to stay motivated and on track</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NaNoWriMoCalculator;